/**
 * WalletBreakdown — "¿de dónde viene tu ISK? ¿a dónde se va?"
 *
 *  - Summary (income / expense / net)
 *  - Por categoría (Combat, Market, Industry, etc.)
 *  - Por ref_type granular (bounty, market_escrow, industry_job_tax, ...)
 *  - Top 5 income / expense entries
 *  - Toggle 7d / 30d
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Category = {
  category: string
  count: number
  income: number
  expense: number
  net: number
}

type RefType = Category & { ref_type: string }

export function WalletBreakdown() {
  const [days, setDays] = useState<7 | 30>(30)
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['wallet-breakdown', forceRefetchKey, days],
    queryFn: () => api.walletBreakdown(days, force),
  })

  const summary = data?.summary
  const byCat: Category[] = data?.by_category ?? []
  const byRef: RefType[] = data?.by_ref_type ?? []
  const topIncome = data?.top_income ?? []
  const topExpense = data?.top_expense ?? []

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Wallet breakdown</div>
          <div className="text-eve-muted text-xs">
            Ingresos y egresos del journal agrupados por categoría — sabé
            exactamente de dónde viene y a dónde se va tu ISK.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <button
              onClick={() => setDays(7)}
              className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                days === 7
                  ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
                  : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
              }`}
            >
              7d
            </button>
            <button
              onClick={() => setDays(30)}
              className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                days === 30
                  ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
                  : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
              }`}
            >
              30d
            </button>
          </div>
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryTile label="Entries" value={String(summary.total_entries)} />
          <SummaryTile
            label="Income"
            value={`${formatISKShort(summary.total_income)} ISK`}
            positive
          />
          <SummaryTile
            label="Expense"
            value={`${formatISKShort(summary.total_expense)} ISK`}
            negative
          />
          <SummaryTile
            label="Net"
            value={`${summary.net >= 0 ? '+' : ''}${formatISKShort(summary.net)} ISK`}
            sub={summary.net >= 0 ? 'verde' : 'rojo'}
            positive={summary.net > 0}
            negative={summary.net < 0}
          />
        </div>
      )}

      {/* Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryColumn title="Por categoría" rows={byCat} />
        <CategoryColumn
          title="Por ref_type (granular)"
          rows={byRef.map((r) => ({ ...r, category: r.ref_type }))}
          extraSub
        />
      </div>

      {/* Top lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <TopList title="Top 5 income" entries={topIncome} positive />
        <TopList title="Top 5 expense" entries={topExpense} positive={false} />
      </div>

      {isLoading && (
        <div className="text-eve-muted py-4 text-center"><LoadingSkeleton variant="default" /></div>
      )}
      {summary && summary.total_entries === 0 && !isLoading && (
        <div className="text-eve-muted py-8 text-center italic">
          Sin transactions en los últimos {days} días.
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function SummaryTile({
  label,
  value,
  sub,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  positive?: boolean
  negative?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function CategoryColumn({
  title,
  rows,
  extraSub,
}: {
  title: string
  rows: Category[]
  extraSub?: boolean
}) {
  if (rows.length === 0) {
    return (
      <div className="border border-eve-border rounded-md p-3">
        <div className="stat-label mb-2">{title}</div>
        <div className="text-eve-muted text-xs italic">Sin datos.</div>
      </div>
    )
  }
  const maxAbs = Math.max(...rows.map((r) => Math.abs(r.net)), 1)
  return (
    <div className="border border-eve-border rounded-md p-3 bg-eve-bg/40">
      <div className="stat-label mb-2">{title}</div>
      <div className="space-y-1.5">
        {rows.slice(0, 12).map((r) => {
          const pct = (Math.abs(r.net) / maxAbs) * 100
          const positive = r.net >= 0
          return (
            <div key={r.category} className="flex items-center gap-2 text-xs">
              <div className="w-32 truncate font-mono">{r.category}</div>
              <div className="flex-1 h-2 bg-eve-border rounded overflow-hidden">
                <div
                  className={`h-full ${positive ? 'bg-eve-success' : 'bg-eve-danger'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div
                className={`w-24 text-right font-mono ${
                  positive ? 'text-eve-success' : 'text-eve-danger'
                }`}
              >
                {positive ? '+' : ''}
                {formatISKShort(r.net)}
              </div>
              {extraSub && (
                <div className="w-16 text-right text-[10px] text-eve-muted font-mono">
                  {r.count}×
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TopList({
  title,
  entries,
  positive,
}: {
  title: string
  entries: Array<{
    id: number
    date: string
    ref_type: string
    category: string
    amount: number
    description: string
  }>
  positive: boolean
}) {
  if (entries.length === 0) {
    return (
      <div className="border border-eve-border rounded-md p-3">
        <div className="stat-label mb-2">{title}</div>
        <div className="text-eve-muted text-xs italic">Sin entries.</div>
      </div>
    )
  }
  return (
    <div className="border border-eve-border rounded-md p-3 bg-eve-bg/40">
      <div className="stat-label mb-2">{title}</div>
      <div className="space-y-2">
        {entries.map((e) => (
          <div
            key={e.id}
            className="flex items-baseline justify-between gap-2 text-xs border-b border-eve-border/30 pb-1.5 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <div className="font-mono text-eve-text">{e.ref_type}</div>
              <div className="text-[10px] text-eve-muted truncate">
                {e.category} · {new Date(e.date).toLocaleDateString('es-AR', { timeZone: 'UTC' })}
              </div>
            </div>
            <div
              className={`font-mono whitespace-nowrap ${positive ? 'text-eve-success' : 'text-eve-danger'}`}
            >
              {positive ? '+' : ''}
              {formatISK(e.amount, 0)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
